/**
 * Copyright 2026 Perform Content Services Limited. Confidential and Proprietary. All Rights Reserved.
 */
import DetectBrowser from 'phenix-web-detect-browser';
import userStore from 'services/user-store/';
import BackendInfoService from 'services/backend-info.service';
import PhenixWebSocket, {requestType, PhenixWebSocketType} from 'services/websocket.provider';
import LoggerFactory from 'services/logger/LoggerFactory';
import {PortalError} from 'utility/error-handler';

export const auth = {
  applicationId: 'applicationId',
  secret: 'secret',
  token: 'token',
  sessionId: 'sessionId'
};

export interface IAuthResponse {
  redirect: null | boolean;
  roles: string[];
  sessionId: string;
  status: string;
}

export const authenticate = async(
  socket: PhenixWebSocketType,
  applicationId: string,
  secret: string,
  previousSessionId = ''
): Promise<{
  response: IAuthResponse;
  applicationId: string;
}> => {
  const logger = LoggerFactory.getLogger('services/auth.service/authenticate');

  if (!socket.isConnected()) {
    logger.error('Socket not connected [%j]', socket);

    throw new PortalError('Socket not connected');
  }

  const browserDetails = new DetectBrowser(navigator.userAgent).detect();

  logger.info('Received browser details: [%j]', browserDetails);

  const data = {
    apiVersion: socket.getApiVersion(),
    clientVersion: BackendInfoService.controlCenterVersion,
    deviceId: '',
    platform: browserDetails.browser,
    platformVersion: browserDetails.version.toString(),
    applicationId,
    authenticationToken: secret,
    sessionId: previousSessionId ? previousSessionId : null
  };

  logger.info('Authenticating with the following parameters: [%j]', data);

  const response = await socket.sendRequest(requestType.authenticate, data);

  if (response.status === 'unauthorized') {
    await Promise.all([
      userStore.set(auth.applicationId, ''),
      userStore.set(auth.secret, ''),
      userStore.set(auth.token, ''),
      userStore.set(auth.sessionId, '')
    ]);

    throw new PortalError('Failed to authenticate. Please check your credentials');
  }

  logger.info('Authenticated [%j]', response);

  await Promise.all([
    userStore.set(auth.applicationId, applicationId),
    userStore.set(auth.secret, btoa(secret)),
    userStore.set(auth.token, btoa(`${applicationId}:${secret}`)),
    userStore.set(auth.sessionId, response.sessionId || '')
  ]);

  socket.setSessionId(response.sessionId);

  return {
    response,
    applicationId
  };
};

export const autoAuthenticate = async(socket: PhenixWebSocket, previousSessionId = ''): Promise<{response: IAuthResponse; applicationId: string}> => {
  const logger = LoggerFactory.getLogger('services/auth.service/autoAuthenticate');
  const [applicationId, secret] = await Promise.all([
    userStore.get(auth.applicationId),
    userStore.get(auth.secret),
    userStore.get(auth.token)
  ]);

  if (!applicationId || !secret) {
    logger.error('Application Id or Secret is missing');

    throw new PortalError('Application Id or Secret is missing');
  }

  return await authenticate(socket, applicationId, atob(secret), previousSessionId);
};

export const clearCredentials = async(): Promise<[void, void, void, void]> => {
  return Promise.all([
    userStore.set(auth.applicationId, ''),
    userStore.set(auth.secret, ''),
    userStore.set(auth.token, ''),
    userStore.set(auth.sessionId, '')
  ]);
};

export const hasLoginToken = async(): Promise<boolean> => {
  const [applicationId, secret] = await Promise.all([
    userStore.get(auth.applicationId),
    userStore.get(auth.secret)
  ]);

  return applicationId && secret ? true : false;
};